import { useQuery } from "@tanstack/react-query";
import { observer } from "mobx-react-lite";
import { AlertTriangle, Building2, Folder } from "lucide-react";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { useOrganizationContext } from "@/lib/context/OrganizationContext";
import { useAuthStore } from "@/hooks/useAuthStore";
import { usePermissions } from "@/hooks/usePermissions";
import { Button } from "@/components/ui/button";
import { DONUT_PALETTE } from "@/components/charts/chartPalette";
import { storageState } from "@/domains/documents/utils/storagePresentation";
import {
  formatUsageBytes,
  usagePercentage,
  type BuildingUsage,
  type ProjectUsage,
} from "./storageAccounting";
import { getBuildingUsage, getUsageBuildings } from "./storageUsageService";

const stateStyles = {
  normal: { fill: "bg-neutral-500", pill: "" },
  near: { fill: "bg-amber-600", pill: "bg-amber-50 text-amber-800" },
  full: { fill: "bg-red-500", pill: "bg-red-50 text-red-700" },
  unlimited: { fill: "", pill: "bg-emerald-50 text-emerald-700" },
};

function UsageStatePill({ state }: { state: ReturnType<typeof storageState> }) {
  const { t } = useLanguage();
  if (state === "normal") return null;
  return (
    <span
      data-testid="usage-state-pill"
      className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ${stateStyles[state].pill}`}
    >
      {state === "near" && (
        <AlertTriangle
          data-testid="usage-warning-icon"
          className="h-3.5 w-3.5"
          aria-hidden="true"
        />
      )}
      {t.settings.usage[state]}
    </span>
  );
}

function usagePercentText(value: number, language: string) {
  return new Intl.NumberFormat(language, { maximumFractionDigits: 1 }).format(value);
}

function UsageBuildingHeader({ usage }: { usage: BuildingUsage }) {
  const { t, language } = useLanguage();
  const words = t.settings.usage;
  const state = storageState(usage.quota);
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
      <Building2 aria-hidden="true" className="h-5 w-5 shrink-0 text-primary" />
      <h2
        data-testid="usage-building-name"
        className="min-w-0 flex-1 break-words text-base font-semibold"
      >
        {usage.name}
      </h2>
      <UsageStatePill state={state} />
      <p
        data-testid="usage-building-value"
        className="ml-auto whitespace-nowrap text-right text-sm text-muted-foreground"
      >
        <strong className="font-semibold text-foreground">
          {formatUsageBytes(usage.quota.usedBytes, language)}
        </strong>{" "}
        {state === "unlimited"
          ? words.used
          : `${words.of} ${formatUsageBytes(usage.quota.limitBytes ?? 0, language)}`}
      </p>
    </div>
  );
}

function UsageBuildingBar({ usage }: { usage: BuildingUsage }) {
  const { t, language } = useLanguage();
  const state = storageState(usage.quota);
  if (state === "unlimited") return null;
  const percentage = usagePercentage(usage.quota.usedBytes, usage.quota.limitBytes);
  return (
    <div>
      <div
        data-testid="usage-building-bar"
        role="progressbar"
        aria-label={usage.name}
        aria-valuenow={Math.min(100, percentage)}
        aria-valuemin={0}
        aria-valuemax={100}
        className="h-[6px] overflow-hidden rounded-[999px] bg-[#ececed]"
      >
        <div
          className={`h-full rounded-[999px] ${stateStyles[state].fill}`}
          style={{ width: `${Math.min(100, percentage)}%` }}
        />
      </div>
      <p data-testid="usage-building-percentage" className="mt-1 text-xs text-muted-foreground">
        {usagePercentText(percentage, language)}% {t.settings.usage.used}
      </p>
    </div>
  );
}

function UsageProjectBar({ project, share }: { project: ProjectUsage; share: number }) {
  return (
    <div
      data-testid="usage-project-bar"
      role="progressbar"
      aria-label={project.name}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.min(100, share)}
      className="h-[5px] overflow-hidden rounded-[999px] bg-[#ececed]"
    >
      <div
        className="h-full rounded-[999px]"
        style={{ width: `${Math.min(100, share)}%`, backgroundColor: DONUT_PALETTE[4] }}
      />
    </div>
  );
}

function UsageProjectRow({ project, denominator }: { project: ProjectUsage; denominator: number }) {
  const { language } = useLanguage();
  const share = usagePercentage(project.usedBytes, denominator);
  return (
    <div
      data-testid="usage-project"
      data-project-id={project.id}
      data-used-bytes={project.usedBytes}
      data-percentage={share}
      data-denominator-bytes={denominator}
      className="grid min-w-0 grid-cols-[minmax(0,1fr)_minmax(28px,1fr)_auto] items-center gap-2 rounded-xl border border-border bg-white px-3 py-3 sm:gap-4"
    >
      <div className="flex min-w-0 items-center gap-2">
        <Folder aria-hidden="true" className="h-4 w-4 shrink-0 text-primary" />
        <span data-testid="usage-project-name" className="truncate text-sm" title={project.name}>
          {project.name}
        </span>
      </div>
      <UsageProjectBar project={project} share={share} />
      <span
        data-testid="usage-project-value"
        className="whitespace-nowrap text-right text-xs text-muted-foreground sm:text-sm"
      >
        {formatUsageBytes(project.usedBytes, language)} · {usagePercentText(share, language)}%
      </span>
    </div>
  );
}

function UsageProjectBreakdown({ usage }: { usage: BuildingUsage }) {
  const { t } = useLanguage();
  const words = t.settings.usage;
  const denominator = usage.quota.limitBytes ?? usage.quota.usedBytes;
  return (
    <div className="space-y-2">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {words.byProject}
      </h3>
      {usage.projects.map((project) => (
        <UsageProjectRow key={project.id} project={project} denominator={denominator} />
      ))}
      {usage.projects.length === 0 && (
        <p className="text-sm text-muted-foreground">{words.noProjects}</p>
      )}
      {usage.overlapBytes > 0 && (
        <p data-testid="usage-shared-storage" className="text-xs text-muted-foreground">
          {words.shared}
        </p>
      )}
    </div>
  );
}

export function UsagePanel({ usage }: { usage: BuildingUsage }) {
  const { t } = useLanguage();
  const pending = usage.quota.activationPending;
  return (
    <section
      data-testid="usage-building"
      data-building-id={usage.id}
      data-storage-state={pending ? "pending" : storageState(usage.quota)}
      className="min-w-0 rounded-2xl bg-[#f7f7f8] p-4 sm:p-6"
      aria-label={usage.name}
    >
      {pending ? (
        <>
          <h2 className="break-words text-base font-semibold">{usage.name}</h2>
          <p role="status" className="mt-2 text-sm text-muted-foreground">
            {t.billing.plans.activationPending}
          </p>
        </>
      ) : (
        <>
          <UsageBuildingHeader usage={usage} />
          <div className="ml-8 mt-3 min-w-0 space-y-4">
            <UsageBuildingBar usage={usage} />
            <UsageProjectBreakdown usage={usage} />
          </div>
        </>
      )}
    </section>
  );
}

function BuildingUsageQuery({ orgId, id, userId }: { orgId: string; id: number; userId: string }) {
  const { t } = useLanguage();
  const query = useQuery({
    queryKey: ["organization-storage-usage", orgId, userId, id],
    queryFn: ({ signal }) => getBuildingUsage(orgId, id, signal),
    staleTime: 30_000,
    refetchOnMount: "always",
  });
  if (query.isError)
    return (
      <div role="alert" className="rounded-xl bg-muted p-4 text-sm">
        <p>{t.settings.usage.loadError}</p>
        <Button variant="outline" className="mt-2" onClick={() => void query.refetch()}>
          {t.settings.usage.retry}
        </Button>
      </div>
    );
  if (!query.data)
    return (
      <p role="status" className="text-sm text-muted-foreground">
        {t.settings.usage.loading}
      </p>
    );
  return <UsagePanel usage={query.data} />;
}

function usageScopeReady(
  orgId: string,
  userId: string,
  organizationReady: boolean,
  rbacReady: boolean,
  isAdmin: boolean
): boolean {
  return Boolean(orgId && userId && organizationReady && rbacReady && isAdmin);
}

function useUsageScope() {
  const { currentOrganization, isReady } = useOrganizationContext();
  const auth = useAuthStore();
  const permissions = usePermissions();
  const orgId = currentOrganization?.id ?? "";
  const userId = auth.userId ?? "";
  return {
    orgId,
    userId,
    enabled: usageScopeReady(
      orgId,
      userId,
      isReady,
      auth.isRbacLoadedFor(orgId),
      permissions.isAdmin
    ),
  };
}

export const OrganizationUsage = observer(function OrganizationUsage() {
  const { t } = useLanguage();
  const { orgId, userId, enabled } = useUsageScope();
  const query = useQuery({
    queryKey: ["organization-storage-buildings", orgId, userId],
    queryFn: ({ signal }) => getUsageBuildings(orgId, signal),
    enabled,
    staleTime: 30_000,
    refetchOnMount: "always",
  });
  if (!enabled || query.isPending) return <p role="status">{t.settings.usage.loading}</p>;
  if (query.isError)
    return (
      <div role="alert">
        <p>{t.settings.usage.loadError}</p>
        <Button onClick={() => void query.refetch()}>{t.settings.usage.retry}</Button>
      </div>
    );
  if (!query.data.length) return <p className="text-muted-foreground">{t.settings.usage.empty}</p>;
  return (
    <div className="space-y-4" data-testid="organization-usage">
      {query.data.map((building) => (
        <BuildingUsageQuery
          key={`${orgId}:${building.id}:${userId}`}
          orgId={orgId}
          id={building.id}
          userId={userId}
        />
      ))}
    </div>
  );
});
